import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BadgeDollarSign, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export default function AckRates() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() as any;
  const [a, setA] = useState<any>(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/agreements/rate-ack/${token}`).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const ack = async () => { setBusy(true); try { const r = await api.post(`/agreements/rate-ack/${token}`, { signature_name: name, agreed }); setA({ ...a, ...r.data }); toast.success("Rate sheet acknowledged"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const isInstaller = user?.role === "installer";
  return (
    <AppShell title="Rate Sheet Update">
      {err && <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="ack-error">{err}</div>}
      {a && (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm" data-testid="ack-document">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 text-xs text-gray-500"><span className="inline-flex items-center gap-1.5"><BadgeDollarSign size={14} className="text-blue-700" />Schedule A — Rate Sheet · effective {a.effective_date}</span><span>Sent {new Date(a.sent_at).toLocaleDateString()} by {a.sent_by?.name}</span></div>
            <iframe title="Rate sheet" className="h-[60vh] w-full bg-white" srcDoc={a.html} data-testid="ack-document-frame" />
          </div>
          <aside>
            {a.status === "acknowledged" ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="ack-complete">
                <div className="flex items-center gap-2 text-sm font-semibold text-green-800"><ShieldCheck size={16} />Acknowledged</div>
                <div className="mt-2 text-xs text-green-900">By <b>{a.ack?.signature_name}</b> on {new Date(a.ack?.at).toLocaleString()} · IP {a.ack?.ip}</div>
                <div className="mt-3"><Button size="sm" variant="outline" data-testid="ack-back-btn" onClick={() => nav(isInstaller ? "/my-jobs" : "/users")}>Back</Button></div>
              </div>
            ) : a.status !== "sent" ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ack-inactive">This rate sheet has been superseded by a newer one. Check My Jobs for the latest.</div>
            ) : !isInstaller ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600" data-testid="ack-admin-view">Admin view — only the installer ({a.sent_to}) can acknowledge.</div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm" data-testid="ack-form">
                <h2 className="text-sm font-semibold text-gray-900">Acknowledge the updated rates</h2>
                <p className="mt-1 text-xs text-gray-500">Under Section 3.1 of your Contractor Agreement, Schedule A may be updated by written agreement. Your acknowledgement is recorded with the time, your IP address and device.</p>
                <div className="mt-3 space-y-3">
                  <div><Label className="text-xs">Full name</Label><Input data-testid="ack-name-input" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} /></div>
                  <label className="flex items-start gap-2 text-xs text-gray-700"><input type="checkbox" data-testid="ack-agree-checkbox" className="mt-0.5 h-4 w-4 accent-blue-700" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />I have reviewed and agree to the updated Schedule A rates, which apply to work orders issued on or after the effective date.</label>
                  <Button className="w-full bg-blue-700 hover:bg-blue-800" data-testid="ack-submit-btn" disabled={busy || !agreed || name.trim().split(" ").length < 2} onClick={ack}>{busy ? "Saving…" : "Acknowledge rates"}</Button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </AppShell>
  );
}
